'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import DebugWidget from './DebugWidget';
import AppTour from './AppTour';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import {
  MessageSquare, Calculator, Users, Bell, Calendar,
  Shield, LogOut, Database, Building2, X, Menu,
  User, RefreshCw, Zap,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const AUTH_ROUTES = ['/auth/login', '/auth/register', '/auth/callback', '/auth/reset-password', '/auth/update-password'];

const ROLE_LABELS: Record<string, string> = {
  free:       'Δωρεάν',
  premium:    'Premium',
  business:   'Business',
  superadmin: 'Admin',
};

const ROLE_BADGE_CLASS: Record<string, string> = {
  free:       'bg-gray-700 text-gray-300',
  premium:    'bg-blue-800 text-blue-200',
  business:   'bg-purple-800 text-purple-200',
  superadmin: 'bg-amber-700 text-amber-100',
};

const ALL_NAV = [
  { href: '/ai-chat',           label: 'Συνομιλία AI',      icon: MessageSquare, roles: ['free','premium','business','superadmin'] },
  { href: '/news',              label: 'Νέα',               icon: Bell,          roles: ['free','premium','business','superadmin'] },
  { href: '/deadlines',         label: 'Ημερολόγιο',        icon: Calendar,      roles: ['free','premium','business','superadmin'] },
  { href: '/tax-simulator',     label: 'Προσομοιωτής',      icon: Calculator,    roles: ['premium','business','superadmin'] },
  { href: '/client-management', label: 'Πελάτες',           icon: Users,         roles: ['business','superadmin'] },
  { href: '/gemi',              label: 'Αναζήτηση Επιχ.',   icon: Building2,     roles: ['premium','business','superadmin'] },
  { href: '/database',          label: 'Βάση Νόμων',        icon: Database,      roles: ['business','superadmin'] },
  { href: '/admin',             label: 'Admin',             icon: Shield,        roles: ['superadmin'] },
  { href: '/pricing',           label: 'Αναβάθμιση',        icon: Zap,           roles: ['free', 'superadmin'] },
];

function MobileTopBar() {
  const pathname  = usePathname();
  const router    = useRouter();
  const { user, profile, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    router.push('/auth/login');
  };

  const visibleNav = ALL_NAV.filter(item =>
    !role ? item.roles.includes('free') : item.roles.includes(role)
  );

  return (
    <>
      {/* Top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-blue-950 text-white border-b border-blue-900 shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm tracking-tight">AskΦόρος</span>
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="p-1.5 rounded-lg hover:bg-blue-900 transition-colors"
          aria-label="Μενού"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Drawer overlay */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Drawer */}
          <div className="relative bg-blue-950 w-72 max-w-[85vw] h-full flex flex-col shadow-2xl">

            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-blue-900">
              <span className="font-semibold text-white text-base">AskΦόρος</span>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* User info */}
            {user && (
              <div className="px-4 py-3 border-b border-blue-900 bg-blue-900/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 rounded-full bg-blue-800 flex items-center justify-center shrink-0">
                    <User size={14} className="text-blue-300" />
                  </div>
                  <span className="text-xs text-blue-200 truncate flex-1">
                    {profile?.email || user.email}
                  </span>
                </div>
                {role && (
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLE_BADGE_CLASS[role] || 'bg-gray-700 text-gray-300'}`}>
                    {ROLE_LABELS[role] || role}
                  </span>
                )}
              </div>
            )}

            {/* Nav items */}
            <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
              {visibleNav.map(item => {
                const Icon = item.icon;
                const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-800 text-white' :'text-blue-200 hover:bg-blue-900 hover:text-white'
                    }`}
                  >
                    <Icon size={17} className="shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Bottom actions */}
            <div className="border-t border-blue-900 p-2 space-y-1 shrink-0">
              {/* Tour replay */}
              <button
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => (window as any).__askforosTour?.(), 300);
                }}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white text-sm transition-all"
              >
                <RefreshCw size={16} className="shrink-0" />
                <span>Ξενάγηση</span>
              </button>

              {/* Logout */}
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-blue-300 hover:bg-red-900/40 hover:text-red-300 text-sm transition-all"
                >
                  <LogOut size={16} className="shrink-0" />
                  <span>Αποσύνδεση</span>
                </button>
              ) : (
                <Link
                  href="/auth/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-blue-300 hover:bg-blue-900 hover:text-white text-sm transition-all"
                >
                  <span>Σύνδεση</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading } = useAuth();
  const isAuthRoute = AUTH_ROUTES.some(r => pathname?.startsWith(r));

  useEffect(() => {
    if (!loading && !isAuthRoute && !user) {
      router.replace(`/auth/login?redirect=${encodeURIComponent(pathname ?? '/')}`);
    }
  }, [loading, user, isAuthRoute, pathname, router]);

  if (isAuthRoute) return <>{children}</>;

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileTopBar />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
        <DebugWidget />
        <AppTour />
      </div>
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <AppShell>{children}</AppShell>
    </AuthProvider>
  );
}
